import React, {
  useMemo, useState, useCallback, useRef, Fragment, RefObject, ReactElement,
} from 'react';
import Card from '../../../../components/card/card.component';
import Table from '../../../../components/table/table.component';
import TableHead from '../../../../components/table-head/table-head.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import Loader from '../../../../components/loader/loader.component';
import List from '../../../../components/list/list.component';
import ListItem from '../../../../components/list-item/list-item.component';
import ListItemText from '../../../../components/list-item-text/list-item-text.component';
import TableSortLabel from '../../../../components/table-sort-label/table-sort-label.component';
import VisibleCell from '../../../../components/visible-cell/visible-cell.component';
import TextItalic from '../../../../components/text-italic/text-italic.component';
import NoContentBlock from '../../../../components/no-content-block/no-content-block.component';
import TableCellExtended from '../../../../components/table-cell-extended/table-cell-extended.component';
import FilterPanel from '../../../../components/filter-panel/filter-panel.component';
import ContentListContainerOffset
  from '../../../app/common/content-list-container-offset/content-list-container-offset.component';
import Controls from '../../../app/controls/controls.component';
import SettingsUserOperationListFilters
  from './settings-user-operation-list-filters/settings-user-operation-list-filters.component';
import SettingsUserOperationFieldList
  from './settings-user-operation-field-list/settings-user-operation-field.component';
import FormatService from '../../../../services/format-service';
import ScreenService from '../../../../services/screen-service';
import useScroll from '../../../../hooks/use-scroll';
import useScreenSmall from '../../../../hooks/use-screen-small';
import useScreenMedium from '../../../../hooks/use-screen-medium';
import { ROUTE_USER } from '../../../../constants/system/routes';
import { SETTINGS_USER_VIEW } from '../../../../constants/entity/permissions';
import { SORT_TYPE_ASC, SORT_TYPE_DESC } from '../../../../constants/system/sort-types';
import { PAGE_LIST_DEFAULT } from '../../../../constants/system/pages';
import { FILTER_VALUE_EMPTY } from '../../../../constants/system/filter-values';
import { FILTER_KEY_NAME } from '../../../../constants/system/filter-keys';
import { FILTER_LABEL_KEY_FULL_NAME } from '../../../../constants/system/filter-label-keys';
import {
  FILTER_TYPE_USER_ID, FILTER_TYPE_OPERATION_TYPE, FILTER_TYPE_ENTITY_TYPE, FILTER_TYPE_CREATE_DATE_FROM,
  FILTER_TYPE_CREATE_DATE_TO,
} from '../../../../constants/system/filter-types';

const SettingsOperationLogUserOperationList: ReactMemoComponent = React.memo((
  props: ReactComponentProps,
): ReactElement => {
  const {
    isLoading,
    userOperationListData,
    userOperationsTotal,
    filterUserList,
    filterUserOperationTypeList,
    filterEntityTypeList,
    onLoadBatchData,
    onSortParamsChange,
    onApplyFilters,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();
  const isMediumScreen: boolean = useScreenMedium();
  const containerRef: RefObject<HTMLDivElement> | null = useRef(null);
  const itemListRef: RefObject<HTMLDivElement> | null = useRef(null);
  const commonUserOperationsTotal: number = (userOperationListData && userOperationListData.length) || 0;

  const DEFAULT_SORT: SortParams = useMemo((): SortParams => ({
    field: 'createdDate',
    type: SORT_TYPE_DESC,
  }), []);

  const DEFAULT_FILTERS: Record<string, FilterValue> = useMemo((): Record<string, FilterValue> => ({
    [FILTER_TYPE_USER_ID]: null,
    [FILTER_TYPE_ENTITY_TYPE]: null,
    [FILTER_TYPE_OPERATION_TYPE]: null,
    [FILTER_TYPE_CREATE_DATE_FROM]: null,
    [FILTER_TYPE_CREATE_DATE_TO]: null,
  }), []);

  const FILTER_OPTION_MAP: Record<string, FilterOption[]> = useMemo((): Record<string, FilterOption[]> => ({
    [FILTER_TYPE_USER_ID]: filterUserList,
    [FILTER_TYPE_ENTITY_TYPE]: filterEntityTypeList,
    [FILTER_TYPE_OPERATION_TYPE]: filterUserOperationTypeList,
  }), [filterUserList, filterEntityTypeList, filterUserOperationTypeList]);

  const FILTER_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_ENTITY_TYPE]: FILTER_KEY_NAME,
    [FILTER_TYPE_OPERATION_TYPE]: FILTER_KEY_NAME,
  }), []);

  const FILTER_LABEL_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_USER_ID]: 'User',
    [FILTER_TYPE_ENTITY_TYPE]: 'Entity Type',
    [FILTER_TYPE_OPERATION_TYPE]: 'Operation',
    [FILTER_TYPE_CREATE_DATE_FROM]: 'Date From',
    [FILTER_TYPE_CREATE_DATE_TO]: 'Date To',
  }), []);

  const FILTER_LABEL_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_USER_ID]: FILTER_LABEL_KEY_FULL_NAME,
  }), []);

  const FILTER_ERROR_MAP_DEFAULT: Record<string, boolean> = useMemo((): Record<string, boolean> => ({
    [FILTER_TYPE_CREATE_DATE_FROM]: false,
    [FILTER_TYPE_CREATE_DATE_TO]: false,
  }), []);

  const [page, setPage]: [number, Function] = useState(PAGE_LIST_DEFAULT);
  const [sortParams, setSortParams]: [SortParams, Function] = useState(DEFAULT_SORT);
  const [filters, setFilters]: [Record<string, FilterValue>, Function] = useState(DEFAULT_FILTERS);
  const [currentFilters, setCurrentFilters]: [Record<string, FilterValue>, Function] = useState(DEFAULT_FILTERS);
  const [filterErrorMap, setFilterErrorMap]: [Record<string, boolean>, Function] = useState(FILTER_ERROR_MAP_DEFAULT);

  const scrollDeps: DepsItem[] = useMemo((): DepsItem[] => [
    userOperationsTotal, commonUserOperationsTotal, isLoading, isLoading, page, sortParams, filters,
  ], [userOperationsTotal, commonUserOperationsTotal, isLoading, isLoading, page, sortParams, filters]);

  const withAllFiltersEmpty: boolean = !filters[FILTER_TYPE_CREATE_DATE_FROM] && !filters[FILTER_TYPE_CREATE_DATE_TO]
    && !filters[FILTER_TYPE_USER_ID] && !filters[FILTER_TYPE_ENTITY_TYPE] && !filters[FILTER_TYPE_OPERATION_TYPE];
  const withFilters: boolean = !withAllFiltersEmpty;
  const isFilterApplyDisabled: boolean = !!Object.values(filterErrorMap)
    .filter((filterWithErrors: boolean): boolean => filterWithErrors).length;

  const tableUserOperationFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'createdDate',
      title: 'Date / Time',
      isSortable: true,
      isNumber: false,
      component: (userOperationItem: UserOperation): ReactElement => (
        <TableCellExtended isText>
          {userOperationItem.createdDate}
        </TableCellExtended>
      ),
    },
    {
      id: 2,
      name: 'user.fullName',
      title: 'User',
      isSortable: true,
      isNumber: false,
      component: (userOperationItem: UserOperation): ReactElement => (
        <TableCell isText>
          {
            userOperationItem.user && (
              <FilteredLink
                withNewWindow
                link={`${ROUTE_USER}/${userOperationItem.user.id}`}
                permission={SETTINGS_USER_VIEW}
              >
                {userOperationItem.user.fullName}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'operationType',
      title: 'Operation',
      isSortable: true,
      isNumber: false,
      component: (userOperationItem: UserOperation): ReactElement => (
        <TableCell isText>
          {userOperationItem.operationTypeText}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'entityType',
      title: 'Object',
      isSortable: true,
      isNumber: false,
      component: (userOperationItem: UserOperation): ReactElement => (
        <TableCellExtended isText>
          {userOperationItem.entityTypeText}
        </TableCellExtended>
      ),
    },
    {
      id: 5,
      name: 'entityName',
      title: 'Name',
      isSortable: false,
      isNumber: false,
      component: (userOperationItem: UserOperation): ReactElement => (
        <TableCell isText>
          {
            userOperationItem.entityLink && userOperationItem.entityName ? (
              <FilteredLink withNewWindow link={userOperationItem.entityLink}>
                {userOperationItem.entityName}
              </FilteredLink>
            ) : (userOperationItem.entityName || (
              <TextItalic>
                None
              </TextItalic>
            ))
          }
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'original',
      title: 'Original Data',
      isSortable: false,
      isNumber: false,
      isHidden: isMediumScreen,
      component: (userOperationItem: UserOperation): ReactElement => (
        <TableCellExtended isText isLarge>
          <SettingsUserOperationFieldList
            isRoot
            userOperationId={userOperationItem.id}
            userOperationEntityFieldsData={userOperationItem.original}
            entityType={userOperationItem.entityType}
          />
        </TableCellExtended>
      ),
    },
    {
      id: 7,
      name: 'result',
      title: 'Result Data',
      isSortable: false,
      isNumber: false,
      isHidden: isMediumScreen,
      component: (userOperationItem: UserOperation): ReactElement => (
        <TableCellExtended isText isLarge>
          <SettingsUserOperationFieldList
            isRoot
            userOperationId={userOperationItem.id}
            userOperationEntityFieldsData={userOperationItem.result}
            entityType={userOperationItem.entityType}
          />
        </TableCellExtended>
      ),
    },
    {
      id: 8,
      name: 'difference',
      title: 'Difference',
      isSortable: false,
      isNumber: false,
      isHidden: isMediumScreen,
      component: (userOperationItem: UserOperation): ReactElement => (
        <TableCellExtended isText isLarge>
          <SettingsUserOperationFieldList
            isRoot
            isDiff
            userOperationId={userOperationItem.id}
            userOperationEntityFieldsData={userOperationItem.original}
            userOperationNewEntityFieldsData={userOperationItem.result}
            entityType={userOperationItem.entityType}
          />
        </TableCellExtended>
      ),
    },
  ], [isMediumScreen]);

  const getOptions: Function = useCallback((
    pageOption?: number,
    sortOption?: number,
    filtersOption?: Record<string, FilterValue>,
  ): RequestOptions => {
    let options: RequestOptions = {};

    options = pageOption ? { ...options, page: pageOption } : options;
    options = sortOption ? { ...options, sort: sortOption } : options;
    options = filtersOption ? { ...options, filters: { ...filtersOption } } : options;

    return options;
  }, []);

  const loadBatchData: Function = useCallback((): void => {
    if (onLoadBatchData) {
      onLoadBatchData(getOptions(page + PAGE_LIST_DEFAULT, sortParams, filters));
    }

    setPage(page + PAGE_LIST_DEFAULT);
  }, [page, sortParams, filters]);

  const onScroll: MouseScrollHandlerFunction = useCallback((): void => {
    if (containerRef && containerRef.current) {
      ScreenService.infiniteScroll(
        containerRef.current.scrollTop,
        containerRef.current.clientHeight,
        itemListRef && itemListRef.current ? itemListRef.current.clientHeight : 0,
        (): void => {
          if (userOperationsTotal > commonUserOperationsTotal && !isLoading) {
            loadBatchData();
          }
        },
        isSmallScreen,
      );
    }
  }, [
    containerRef, itemListRef, userOperationsTotal, page, sortParams, filters, commonUserOperationsTotal, isLoading,
    isSmallScreen,
  ]);

  const sortTableRows: Function = useCallback((columnName: string): void => {
    const sortType: SortType = sortParams.field === columnName && sortParams.type === SORT_TYPE_DESC
      ? SORT_TYPE_ASC : SORT_TYPE_DESC;

    const sortParamsValue: SortParams = {
      field: columnName,
      type: sortType,
    };

    setPage(PAGE_LIST_DEFAULT);
    setSortParams(sortParamsValue);

    if (onSortParamsChange) {
      onSortParamsChange(getOptions(null, sortParamsValue, filters));
    }
  }, [page, sortParams, filters]);

  const applyFilters: Function = useCallback((newFilters: Record<string, FilterValue>): void => {
    const resultNewFilters: Record<string, FilterValue> = newFilters || currentFilters;

    setFilters({ ...resultNewFilters });
    setPage(PAGE_LIST_DEFAULT);

    if (onApplyFilters && !isFilterApplyDisabled) {
      onApplyFilters(getOptions(null, sortParams, resultNewFilters));
    }
  }, [currentFilters, sortParams, isFilterApplyDisabled]);

  const onResetFilters: Function = useCallback((): void => {
    setCurrentFilters({ ...filters });
  }, [filters]);

  const setFilterValue: Function = useCallback((filterType: string, value: string): Record<string, FilterValue> => {
    const selectedValue: FilterValue = FormatService.parseInt(value) !== FILTER_VALUE_EMPTY ? value : null;
    const newFilters: Record<string, FilterValue> = { ...currentFilters, [filterType]: selectedValue };

    setCurrentFilters(newFilters);

    return newFilters;
  }, [currentFilters]);

  const onFilterError: Function = useCallback((filterType: string, error: string): void => {
    if (filterType) {
      setFilterErrorMap({
        ...filterErrorMap,
        [filterType]: !!error,
      });
    }
  }, [filterErrorMap]);

  const onDeleteFilterValue: Function = useCallback((filterType: string): void => {
    const newFilters: Record<string, FilterValue> = setFilterValue(filterType, null);

    applyFilters({ ...newFilters });
  }, [currentFilters, sortParams]);

  useScroll(onScroll, scrollDeps);

  return (
    <Fragment>
      <Controls
        withNoEditMode
        withFilters
        isFilterApplyDisabled={isFilterApplyDisabled}
        withAllFiltersEmpty={withAllFiltersEmpty}
        filters={filters}
        currentFilters={currentFilters}
        onApplyFilters={applyFilters}
        onResetFilters={onResetFilters}
        filterComponent={(
          <SettingsUserOperationListFilters
            filters={filters}
            currentFilters={currentFilters}
            filterErrorMap={filterErrorMap}
            filterUserList={filterUserList}
            filterUserOperationTypeList={filterUserOperationTypeList}
            filterEntityTypeList={filterEntityTypeList}
            onSetFilterValue={setFilterValue}
            onFilterError={onFilterError}
          />
        )}
      />
      <FilterPanel
        filters={filters}
        filterOptionMap={FILTER_OPTION_MAP}
        filterKeyMap={FILTER_KEY_MAP}
        filterLabelMap={FILTER_LABEL_MAP}
        filterLabelKeyMap={FILTER_LABEL_KEY_MAP}
        onDeleteFilterValue={onDeleteFilterValue}
      />
      {
        userOperationListData && !!userOperationListData.length ? (
          <Card>
            <ContentListContainerOffset
              withTabs
              withSubTabs
              isDefault={isSmallScreen}
              withFilters={withFilters}
              ref={containerRef}
              onScroll={!isSmallScreen ? onScroll : undefined}
            >
              <div ref={itemListRef}>
                {
                  isSmallScreen && (
                    <List>
                      {
                        userOperationListData.map((userOperationItem: UserOperation): ReactElement => (
                          <ListItem isExtended key={userOperationItem.id}>
                            <ListItemText isBlock>
                              {userOperationItem.createdDate}
                            </ListItemText>
                            <ListItemText
                              isBlock
                              subText={userOperationItem.operationTypeText}
                            >
                              {userOperationItem.user ? userOperationItem.user.fullName : ''}
                            </ListItemText>
                            <ListItemText
                              isBlock
                              isBlockRight
                              subText={userOperationItem.entityName}
                            >
                              {userOperationItem.entityTypeText}
                            </ListItemText>
                          </ListItem>
                        ))
                      }
                    </List>
                  )
                }
                {
                  !isSmallScreen && (
                    <Table withStickyHeader>
                      <TableHead isHeader withFixedCellsSize>
                        <TableRow>
                          {
                            tableUserOperationFields.map((tableField: TableField): ReactElement | boolean => (
                              <VisibleCell key={tableField.id} tableField={tableField}>
                                <TableCell
                                  sortDirection={(
                                    sortParams && tableField.isSortable && sortParams.field === tableField.name
                                      ? sortParams.type : false
                                  )}
                                >
                                  {
                                    tableField.isSortable ? (
                                      <TableSortLabel
                                        isActive={sortParams.field === tableField.name}
                                        sortType={sortParams.type}
                                        onClick={(): void => {
                                          sortTableRows(tableField.name);
                                        }}
                                      >
                                        {tableField.title}
                                      </TableSortLabel>
                                    ) : tableField.title
                                  }
                                </TableCell>
                              </VisibleCell>
                            ))
                          }
                        </TableRow>
                      </TableHead>
                      <TableBody isPlain withZebraRows withFixedCellsSize withBorderedCells>
                        {
                          userOperationListData.map((userOperationItem: UserOperation): ReactElement => (
                            <TableRow withAlignTop key={userOperationItem.id}>
                              {
                                tableUserOperationFields.map((tableField: TableField): ReactElement | boolean => (
                                  <VisibleCell key={tableField.id} tableField={tableField}>
                                    {!!tableField.component && tableField.component(userOperationItem)}
                                  </VisibleCell>
                                ))
                              }
                            </TableRow>
                          ))
                        }
                      </TableBody>
                    </Table>
                  )
                }
                {
                  isLoading && (
                    <Loader isInline />
                  )
                }
              </div>
            </ContentListContainerOffset>
          </Card>
        ) : (
          <NoContentBlock>
            No operations found by your request
          </NoContentBlock>
        )
      }
    </Fragment>
  );
});

export default SettingsOperationLogUserOperationList;
